import { supabase } from '../lib/supabase';

export const adminUserService = {
  // Parse CSV/Excel data
  parseImportData(csvText) {
    const lines = csvText?.trim()?.split('\n');
    const headers = lines?.[0]?.split(',')?.map(h => h?.trim());
    
    const users = [];
    for (let i = 1; i < lines?.length; i++) {
      const values = lines?.[i]?.split(',')?.map(v => v?.trim());
      if (values?.length >= 2) {
        users?.push({
          email: values?.[0],
          fullName: values?.[1],
          valuebookLink: values?.[2] || null
        });
      }
    }
    
    return users;
  },

  // Parse Excel data (array of arrays from xlsx)
  parseExcelData(jsonData) {
    if (!jsonData || jsonData?.length < 2) {
      throw new Error('Excel file is empty or invalid');
    }

    const users = [];
    
    // Skip header row (index 0), process data rows
    for (let i = 1; i < jsonData?.length; i++) {
      const row = jsonData?.[i];
      
      // Skip empty rows
      if (!row || row?.length === 0) continue;
      
      // Extract email and full_name (required), valuebook_link (optional)
      const email = row?.[0]?.toString()?.trim();
      const fullName = row?.[1]?.toString()?.trim();
      const valuebookLink = row?.[2] ? row?.[2]?.toString()?.trim() : null;
      
      // Validate required fields
      if (email && fullName) {
        users?.push({
          email,
          fullName,
          valuebookLink
        });
      }
    }
    
    if (users?.length === 0) {
      throw new Error('No valid user data found in Excel file');
    }
    
    return users;
  },

  // Create import batch
  async createImportBatch(fileName, totalRecords) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('user_import_batches')?.insert({
        admin_id: user?.id,
        file_name: fileName,
        total_records: totalRecords,
        import_status: 'pending'
      })?.select()?.single();

    if (error) throw error;

    return {
      id: data?.id,
      adminId: data?.admin_id,
      fileName: data?.file_name,
      totalRecords: data?.total_records,
      importStatus: data?.import_status,
      createdAt: data?.created_at
    };
  },

  // Generate temporary password (client-side)
  generateTempPassword() {
    const chars = 'ABCDEFGHJKMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
    let password = '';
    for (let i = 0; i < 12; i++) {
      password += chars?.charAt(Math.floor(Math.random() * chars?.length));
    }
    return password;
  },

  async getUserAuditLog(userId) {
    const { data, error } = await supabase?.from('audit_logs')?.select('*')?.eq('user_id', userId)?.order('created_at', { ascending: false });

    if (error) throw error;
    return data;
  },

  // Email Template Management
  async getEmailTemplates() {
    const { data, error } = await supabase?.from('email_templates')?.select('*')?.order('created_at', { ascending: false });

    if (error) throw error;
    
    // Map database snake_case to JavaScript camelCase
    return data?.map(template => ({
      id: template?.id,
      name: template?.template_name,
      subject: template?.subject,
      body: template?.body_template,
      bodyTemplate: template?.body_template, // Support both for backward compatibility
      category: template?.category || 'invitation',
      isDefault: template?.is_default,
      createdBy: template?.created_by,
      createdAt: template?.created_at,
      updatedAt: template?.updated_at
    }));
  },

  async createEmailTemplate(templateData) {
    const { data, error } = await supabase?.from('email_templates')?.insert([{
        template_name: templateData?.name || templateData?.templateName,
        subject: templateData?.subject,
        body_template: templateData?.body || templateData?.bodyTemplate,
        category: templateData?.category || 'invitation',
        is_default: templateData?.isDefault || false,
        variables: templateData?.variables || []
      }])?.select()?.single();

    if (error) throw error;
    
    // Map back to camelCase
    return {
      id: data?.id,
      name: data?.template_name,
      subject: data?.subject,
      body: data?.body_template,
      bodyTemplate: data?.body_template,
      category: data?.category,
      isDefault: data?.is_default,
      createdBy: data?.created_by,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  },

  async updateEmailTemplate(templateId, templateData) {
    const { data, error } = await supabase?.from('email_templates')?.update({
        template_name: templateData?.name || templateData?.templateName,
        subject: templateData?.subject,
        body_template: templateData?.body || templateData?.bodyTemplate,
        category: templateData?.category,
        is_default: templateData?.isDefault,
        variables: templateData?.variables || [],
        updated_at: new Date()?.toISOString()
      })?.eq('id', templateId)?.select()?.single();

    if (error) throw error;
    
    // Map back to camelCase
    return {
      id: data?.id,
      name: data?.template_name,
      subject: data?.subject,
      body: data?.body_template,
      bodyTemplate: data?.body_template,
      category: data?.category,
      isDefault: data?.is_default,
      createdBy: data?.created_by,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  },

  async deleteEmailTemplate(templateId) {
    const { error } = await supabase?.from('email_templates')?.delete()?.eq('id', templateId);

    if (error) throw error;
    return true;
  },

  async getTemplateById(templateId) {
    const { data, error } = await supabase?.from('email_templates')?.select('*')?.eq('id', templateId)?.single();

    if (error) throw error;
    
    // Map database snake_case to JavaScript camelCase
    return {
      id: data?.id,
      name: data?.template_name,
      subject: data?.subject,
      body: data?.body_template,
      bodyTemplate: data?.body_template,
      category: data?.category || 'invitation',
      isDefault: data?.is_default,
      createdBy: data?.created_by,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  },

  // Render template with actual data
  renderEmailTemplate(template, invitationData) {
    let subject = template?.subject;
    let body = template?.bodyTemplate || template?.body_template || template?.body;

    // Replace placeholders
    subject = subject?.replace(/\{\{full_name\}\}/g, invitationData?.fullName);
    subject = subject?.replace(/\{\{email\}\}/g, invitationData?.email);

    body = body?.replace(/\{\{full_name\}\}/g, invitationData?.fullName);
    body = body?.replace(/\{\{email\}\}/g, invitationData?.email);
    body = body?.replace(/\{\{temporary_password\}\}/g, invitationData?.temporaryPassword);
    body = body?.replace(/\{\{valuebook_link\}\}/g, invitationData?.valuebookLink || '');

    return { subject, body };
  },

  // Create user invitations (updated to support template)
  async createInvitations(batchId, users, templateId = null) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const invitations = users?.map(u => ({
      batch_id: batchId,
      admin_id: user?.id,
      email: u?.email,
      full_name: u?.fullName,
      valuebook_link: u?.valuebookLink,
      temporary_password: this.generateTempPassword(),
      invitation_status: 'pending',
      template_id: templateId,
      expires_at: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)?.toISOString()
    }));

    const { data, error } = await supabase?.from('user_invitations')?.insert(invitations)?.select();

    if (error) throw error;

    return data?.map(inv => ({
      id: inv?.id,
      batchId: inv?.batch_id,
      email: inv?.email,
      fullName: inv?.full_name,
      valuebookLink: inv?.valuebook_link,
      temporaryPassword: inv?.temporary_password,
      invitationStatus: inv?.invitation_status,
      templateId: inv?.template_id,
      expiresAt: inv?.expires_at,
      createdAt: inv?.created_at
    }));
  },

  // Create auth users from invitations
  async createAuthUsers(invitations) {
    const results = { successful: 0, failed: 0, errors: [] };

    for (const inv of invitations) {
      try {
        const { error } = await supabase?.auth?.signUp({
          email: inv?.email,
          password: inv?.temporaryPassword,
          options: {
            data: {
              full_name: inv?.fullName,
              valuebook_link: inv?.valuebookLink || '',
              role: 'user'
            }
          }
        });

        if (error) throw error;
        results.successful++;
      } catch (err) {
        results.failed++;
        results?.errors?.push({ email: inv?.email, error: err?.message });
      }
    }

    return results;
  },

  // Generate mailto link (updated to support custom templates)
  async generateMailtoLinkWithTemplate(email, fullName, tempPassword, valuebookLink, templateId = null) {
    let template;

    if (templateId) {
      const { data } = await supabase?.from('email_templates')?.select('*')?.eq('id', templateId)?.single();
      
      template = data ? {
        subject: data?.subject,
        bodyTemplate: data?.body_template
      } : null;
    }

    if (!template) {
      // Use default template
      const { data } = await supabase?.from('email_templates')?.select('*')?.eq('is_default', true)?.single();

      template = data ? {
        subject: data?.subject,
        bodyTemplate: data?.body_template
      } : {
        subject: 'Invitation to ValueBook Marketplace',
        bodyTemplate: `Hello {{full_name}},

You have been invited to join ValueBook Marketplace.

Your temporary login credentials:
Email: {{email}}
Password: {{temporary_password}}

Please login and change your password immediately.

Best regards,
ValueBook Marketplace Team`
      };
    }

    const rendered = this.renderEmailTemplate(template, {
      fullName,
      email,
      temporaryPassword: tempPassword,
      valuebookLink
    });

    let subject = encodeURIComponent(rendered?.subject);
    let body = encodeURIComponent(rendered?.body);

    return `mailto:${email}?subject=${subject}&body=${body}`;
  },

  // Update batch status
  async updateBatchStatus(batchId, status, successCount, failCount, errors) {
    const { error } = await supabase?.from('user_import_batches')?.update({
        import_status: status,
        successful_imports: successCount,
        failed_imports: failCount,
        error_details: errors ? { errors } : null,
        completed_at: new Date()?.toISOString()
      })?.eq('id', batchId);

    if (error) throw error;
  },

  // Update invitation status
  async updateInvitationStatus(invitationId, status) {
    const { error } = await supabase?.from('user_invitations')?.update({
        invitation_status: status,
        sent_at: status === 'sent' ? new Date()?.toISOString() : null
      })?.eq('id', invitationId);

    if (error) throw error;
  },

  // Get import batches
  async getImportBatches() {
    const { data, error } = await supabase?.from('user_import_batches')?.select('*')?.order('created_at', { ascending: false });

    if (error) throw error;

    return data?.map(batch => ({
      id: batch?.id,
      fileName: batch?.file_name,
      totalRecords: batch?.total_records,
      successfulImports: batch?.successful_imports,
      failedImports: batch?.failed_imports,
      importStatus: batch?.import_status,
      errorDetails: batch?.error_details,
      createdAt: batch?.created_at,
      completedAt: batch?.completed_at
    }));
  },

  // Get invitations by batch (updated to include template info)
  async getInvitationsByBatch(batchId) {
    const { data, error } = await supabase?.from('user_invitations')?.select(`
        *,
        email_templates:template_id (
          template_name,
          subject
        )
      `)?.eq('batch_id', batchId)?.order('created_at', { ascending: false });

    if (error) throw error;

    return data?.map(inv => ({
      id: inv?.id,
      email: inv?.email,
      fullName: inv?.full_name,
      valuebookLink: inv?.valuebook_link,
      temporaryPassword: inv?.temporary_password,
      invitationStatus: inv?.invitation_status,
      sentAt: inv?.sent_at,
      acceptedAt: inv?.accepted_at,
      expiresAt: inv?.expires_at,
      templateId: inv?.template_id,
      templateName: inv?.email_templates?.template_name || 'Default',
      createdAt: inv?.created_at
    }));
  },

  // Get invitation statistics
  async getInvitationStats(batchId = null) {
    let query = supabase?.from('user_invitations')?.select('invitation_status');

    if (batchId) {
      query = query?.eq('batch_id', batchId);
    }

    const { data, error } = await query;

    if (error) throw error;

    const stats = {
      total: data?.length || 0,
      pending: 0,
      sent: 0,
      accepted: 0,
      expired: 0
    };

    data?.forEach(inv => {
      stats[inv?.invitation_status] = (stats?.[inv?.invitation_status] || 0) + 1;
    });

    return stats;
  },

  // Get all pending invitations
  async getPendingInvitations() {
    const { data, error } = await supabase?.from('user_invitations')?.select('*')?.eq('invitation_status', 'pending')?.order('created_at', { ascending: false });

    if (error) throw error;

    return data?.map(inv => ({
      id: inv?.id,
      email: inv?.email,
      fullName: inv?.full_name,
      temporaryPassword: inv?.temporary_password,
      invitationStatus: inv?.invitation_status,
      expiresAt: inv?.expires_at,
      createdAt: inv?.created_at
    }));
  },

  // ─── Admin Action: Send Email via Edge Function ───────────────────────────
  async sendAdminEmail({ recipients, subject, htmlBody, textBody = '', actionType = 'custom' }) {
    const { data, error } = await supabase?.functions?.invoke('send-admin-email', {
      body: { recipients, subject, htmlBody, textBody, actionType }
    });
    if (error) throw error;
    // Surface Resend-level errors even when HTTP 200 is returned
    if (data?.failed > 0 && data?.sent === 0) {
      const firstErr = data?.errors?.[0]?.error || 'Email nije poslat';
      throw new Error(`Resend greška: ${firstErr}`);
    }
    return data;
  },

  // ─── Admin Action: Suspend user (1st level block) ─────────────────────────
  async suspendUser(userId, reason, adminId) {
    const { error } = await supabase
      ?.from('user_profiles')
      ?.update({
        is_suspended: true,
        banned_reason: reason || null,
        banned_at: new Date()?.toISOString(),
        banned_by: adminId || null,
        updated_at: new Date()?.toISOString()
      })
      ?.eq('id', userId);
    if (error) {
      console.error('[suspendUser] Failed to suspend user:', error);
      throw error;
    }
    return { success: true };
  },

  // ─── Admin Action: Ban + archive user (2nd level) ─────────────────────────
  async banAndArchiveUser(userId, reason, adminId) {
    const now = new Date()?.toISOString();
    const steps = [];
    let partialFailure = false;

    // Step 1: Ban user in user_profiles
    const { error: banError } = await supabase
      ?.from('user_profiles')
      ?.update({
        is_banned: true,
        is_suspended: true,
        banned_reason: reason || null,
        banned_at: now,
        banned_by: adminId || null,
        archived_at: now,
        updated_at: now
      })
      ?.eq('id', userId);
    if (banError) {
      console.error('[banAndArchiveUser] Step 1 - Failed to ban user:', banError);
      throw banError;
    }
    steps?.push('user_profiles: banned');

    // Step 2: Archive marketplace_wishes
    const { error: wishesError } = await supabase
      ?.from('marketplace_wishes')
      ?.update({ is_archived: true, archived_at: now })
      ?.eq('user_id', userId)
      ?.eq('is_archived', false);
    if (wishesError) {
      console.error('[banAndArchiveUser] Step 2 - Failed to archive marketplace_wishes:', wishesError);
      partialFailure = true;
      steps?.push('marketplace_wishes: FAILED - ' + wishesError?.message);
    } else {
      steps?.push('marketplace_wishes: archived');
    }

    // Step 3: Archive negotiations (initiator or responder)
    const { error: negotiationsError } = await supabase
      ?.from('negotiations')
      ?.update({ is_archived: true, archived_at: now })
      ?.or(`initiator_id.eq.${userId},responder_id.eq.${userId}`)
      ?.eq('is_archived', false);
    if (negotiationsError) {
      console.error('[banAndArchiveUser] Step 3 - Failed to archive negotiations:', negotiationsError);
      partialFailure = true;
      steps?.push('negotiations: FAILED - ' + negotiationsError?.message);
    } else {
      steps?.push('negotiations: archived');
    }

    // Step 4: Archive user_ratings (rater or rated)
    const { error: ratingsError } = await supabase
      ?.from('user_ratings')
      ?.update({ is_archived: true, archived_at: now })
      ?.or(`rater_id.eq.${userId},rated_id.eq.${userId}`)
      ?.eq('is_archived', false);
    if (ratingsError) {
      console.error('[banAndArchiveUser] Step 4 - Failed to archive user_ratings:', ratingsError);
      partialFailure = true;
      steps?.push('user_ratings: FAILED - ' + ratingsError?.message);
    } else {
      steps?.push('user_ratings: archived');
    }

    return { success: true, partialFailure, steps };
  },

  // ─── Admin Action: Unsuspend user (lift suspension only) ─────────────────
  async unsuspendUser(userId) {
    const { error } = await supabase
      ?.from('user_profiles')
      ?.update({
        is_suspended: false,
        banned_reason: null,
        banned_at: null,
        banned_by: null,
        updated_at: new Date()?.toISOString()
      })
      ?.eq('id', userId);
    if (error) {
      console.error('[unsuspendUser] Failed to unsuspend user:', error);
      throw error;
    }
    return { success: true };
  },

  // ─── Admin Action: Unban user + restore archived activities ───────────────
  async unbanUser(userId) {
    const steps = [];
    let partialFailure = false;

    // Step 1: Unban user in user_profiles
    const { error: unbanError } = await supabase
      ?.from('user_profiles')
      ?.update({
        is_banned: false,
        is_suspended: false,
        banned_reason: null,
        banned_at: null,
        banned_by: null,
        archived_at: null,
        updated_at: new Date()?.toISOString()
      })
      ?.eq('id', userId);
    if (unbanError) {
      console.error('[unbanUser] Step 1 - Failed to unban user:', unbanError);
      throw unbanError;
    }
    steps?.push('user_profiles: unbanned');

    // Step 2: Restore marketplace_wishes
    const { error: wishesError } = await supabase
      ?.from('marketplace_wishes')
      ?.update({ is_archived: false, archived_at: null })
      ?.eq('user_id', userId)
      ?.eq('is_archived', true);
    if (wishesError) {
      console.error('[unbanUser] Step 2 - Failed to restore marketplace_wishes:', wishesError);
      partialFailure = true;
      steps?.push('marketplace_wishes: FAILED - ' + wishesError?.message);
    } else {
      steps?.push('marketplace_wishes: restored');
    }

    // Step 3: Restore negotiations
    const { error: negotiationsError } = await supabase
      ?.from('negotiations')
      ?.update({ is_archived: false, archived_at: null })
      ?.or(`initiator_id.eq.${userId},responder_id.eq.${userId}`)
      ?.eq('is_archived', true);
    if (negotiationsError) {
      console.error('[unbanUser] Step 3 - Failed to restore negotiations:', negotiationsError);
      partialFailure = true;
      steps?.push('negotiations: FAILED - ' + negotiationsError?.message);
    } else {
      steps?.push('negotiations: restored');
    }

    // Step 4: Restore user_ratings
    const { error: ratingsError } = await supabase
      ?.from('user_ratings')
      ?.update({ is_archived: false, archived_at: null })
      ?.or(`rater_id.eq.${userId},rated_id.eq.${userId}`)
      ?.eq('is_archived', true);
    if (ratingsError) {
      console.error('[unbanUser] Step 4 - Failed to restore user_ratings:', ratingsError);
      partialFailure = true;
      steps?.push('user_ratings: FAILED - ' + ratingsError?.message);
    } else {
      steps?.push('user_ratings: restored');
    }

    return { success: true, partialFailure, steps };
  },

  // ─── Legacy alias kept for backward compatibility ─────────────────────────
  async reinstateUser(userId) {
    return this.unbanUser(userId);
  }
};

export default adminUserService;